import clsx from 'clsx';
import {localizeTo} from '@app/i18n/localize-link';
import {LocaleSwitcher} from '@common/locale-switcher/locale-switcher';
import {Button} from '@ui/buttons/button';
import {Trans} from '@ui/i18n/trans';
import {useSettings} from '@ui/settings/use-settings';
import {Logo} from '@common/ui/navigation/navbar/logo';
import {Link} from 'react-router';
import {FacebookIcon} from '@ui/icons/social/facebook';
import {TwitterIcon} from '@ui/icons/social/twitter';
import {InstagramIcon} from '@ui/icons/social/instagram';
import {YoutubeIcon} from '@ui/icons/social/youtube';
import {TiktokIcon} from '@ui/icons/social/tiktok';

interface Props {
  className?: string;
  padding?: string;
}

export function Footer({className, padding}: Props) {
  const year = new Date().getFullYear();
  const {branding} = useSettings();
  return (
    <footer
      className={clsx(
        'text-[15px] text-white/78',
        padding ? padding : 'pb-26 pt-56 md:pb-46',
        'relative overflow-hidden border-t border-white/10 bg-transparent px-2 shadow-none',
        className,
      )}
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-[-8%] top-0 h-44 w-44 rounded-full bg-yellow-400/10 blur-3xl" />
        <div className="absolute bottom-[-30%] right-[4%] h-52 w-52 rounded-full bg-yellow-300/12 blur-3xl" />
      </div>

      <section className="grid gap-20 border-y border-white/10 py-18 md:grid-cols-[1.5fr_1fr_1fr_1fr] md:py-24">
        <div className="pr-6">
          <Logo size="h-18 md:h-20" color="light" />
          <p className="mt-10 max-w-520 text-[0.98rem] leading-7 text-white/70">
            CINE5, film ve dizi dünyasını tek çatı altında toplayan yeni nesil
            bir keşif platformudur. Kullanıcılar, en popüler filmleri ve
            dizileri inceleyebilir, oyuncu kadrolarını keşfedebilir ve
            içeriklerin hangi platformda izlenebileceğini kolayca öğrenebilir.
          </p>
          <p className="mt-6 max-w-520 text-[0.93rem] leading-6 text-white/55">
            CINE5, "ne izlesem?" sorusuna hızlı ve doğru cevap vermek için
            tasarlanmıştır.
          </p>
          <p className="mt-10 text-xs uppercase tracking-[0.16em] text-white/45">
            CINE5 Entertainment Platform
          </p>
        </div>

        <nav aria-label="Hızlı bağlantılar">
          <p className="mb-8 text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
            Keşfet
          </p>
          <div className="flex flex-col gap-8 text-[0.96rem]">
            <Link className="footer-link-minimal" to={localizeTo('/tr/filmler')}>
              Filmler
            </Link>
            <Link className="footer-link-minimal" to={localizeTo('/tr/diziler')}>
              Diziler
            </Link>
            <Link className="footer-link-minimal" to={localizeTo('/tr/oyuncular')}>
              Oyuncular
            </Link>
            <Link className="footer-link-minimal" to={localizeTo('/tr/haberler')}>
              Haberler
            </Link>
            <a
              className="footer-link-minimal"
              href="/sitemap.xml"
              target="_blank"
              rel="noopener noreferrer"
            >
              Site Haritası
            </a>
          </div>
        </nav>

        <nav aria-label="Platform bağlantıları">
          <p className="mb-8 text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
            Platform
          </p>
          <div className="flex flex-col gap-8 text-[0.96rem]">
            <Link className="footer-link-minimal" to={localizeTo('/tr/vizyondakiler')}>
              Vizyondakiler
            </Link>
            <Link className="footer-link-minimal" to={localizeTo('/tr/yakinda')}>
              Yakında
            </Link>
            <Link className="footer-link-minimal" to="/listeler">
              Listeler
            </Link>
            <Link className="footer-link-minimal" to={localizeTo('/tr/ara')}>
              Arama
            </Link>
          </div>
        </nav>

        <div>
          <p className="mb-8 text-xs font-semibold uppercase tracking-[0.16em] text-white/45">
            Kurumsal
          </p>
          <div className="flex flex-col gap-8 text-[0.96rem]">
            <Link className="footer-link-minimal" to="/sayfalar/gizlilik-politikasi">
              <Trans message="Gizlilik Politikası" />
            </Link>
            <Link className="footer-link-minimal" to="/sayfalar/kullanim-sartlari">
              <Trans message="Kullanım Şartları" />
            </Link>
            <Link className="footer-link-minimal" to="/iletisim">
              <Trans message="İletişim" />
            </Link>
            <Link className="footer-link-minimal" to="/sayfalar/hakkimizda">
              Hakkımızda
            </Link>
          </div>

          <div className="mt-12 flex items-center gap-10">
            <SocialLink
              href="https://www.youtube.com/@Cine5com"
              label="CINE5 YouTube"
            >
              <YoutubeIcon />
            </SocialLink>
            <SocialLink
              href="https://www.instagram.com/cine5com/"
              label="CINE5 Instagram"
            >
              <InstagramIcon />
            </SocialLink>
            <SocialLink href="https://x.com/Cine5com" label="CINE5 X (Twitter)">
              <TwitterIcon />
            </SocialLink>
            <SocialLink
              href="https://www.tiktok.com/@cine5com"
              label="CINE5 TikTok"
            >
              <TiktokIcon />
            </SocialLink>
            <SocialLink
              href="https://www.facebook.com/cine5com"
              label="CINE5 Facebook"
            >
              <FacebookIcon />
            </SocialLink>
          </div>
        </div>
      </section>

      <div className="mt-16 items-center justify-between gap-16 text-center md:flex md:text-left">
        <span className="text-white/55">
          <Trans
            message="Copyright © :year :name, All Rights Reserved"
            values={{year, name: branding.site_name}}
          />
        </span>
        <div className="flex items-center justify-center gap-10">
          <Button
            elementType={Link}
            to={localizeTo('/tr/haberler')}
            variant="flat"
            className="min-h-34 rounded-full bg-white/8 px-12 text-xs text-white/88 hover:bg-white/14"
          >
            Haber Merkezi
          </Button>
          <LocaleSwitcher />
        </div>
      </div>
    </footer>
  );
}

interface SocialLinkProps {
  href: string;
  label: string;
  children: React.ReactNode;
}
function SocialLink({href, label, children}: SocialLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex h-34 w-34 items-center justify-center rounded-full border border-white/15 bg-white/[0.02] text-white/72 transition hover:border-yellow-300/70 hover:bg-yellow-300/12 hover:text-yellow-100"
    >
      {children}
    </a>
  );
}

